import { StrictMode, useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Link, Route, Routes, useLocation, useParams } from 'react-router-dom';
import './styles/tokens.css';
import './styles/site.css';
import { App } from './App';
import { AboutPage, ContactPage } from './pages/about';
import { DownloadPage } from './pages/download';
import { EulaPage, PrivacyPage, RefundPage } from './pages/legal';
import { JoinedPage } from './sections/joined';
import { ThanksPage } from './pages/thanks';
import { AccountPage } from './pages/account';
import { TrialPage } from './pages/trial';
import { AffiliatesPage } from './pages/affiliates';
import { VariantsPage } from './pages/variants';
import { SceneLabPage } from './pages/scene-lab';
import { SiteNav } from './sections/hero';
import { BigFooter } from './sections/footer';
import { DocsShell } from './docs/DocsShell';
import { DocPageView } from './docs/DocPageView';
import { DOCS } from './docs/registry';
import { SeoPageView } from './seo-pages/render';
import { BlogIndex, CompareIndex } from './seo-pages/blog-index';
import { pageBySlug } from './seo-pages/registry';
import { NotMePage } from './pages/notme';
import { ConsentBanner } from './components/consent-banner';
import { initTracking, track } from './track';

// One scroll brain for the whole site: hash → scroll to anchor (with nav offset),
// route change without hash → back to top.
function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      // wait one frame so the target page has rendered
      const t = window.setTimeout(() => {
        const el = document.querySelector(hash);
        if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 76, behavior: 'smooth' });
      }, 60);
      return () => window.clearTimeout(t);
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

// Funnel beacon (src/track.ts): first-touch capture on boot, then one
// page_view per route change (docs_view on docs pages, for the docs panel).
function TrackManager() {
  const { pathname } = useLocation();
  useEffect(() => { initTracking(); }, []);
  useEffect(() => {
    track(pathname.startsWith('/docs') ? 'docs_view' : 'page_view');
  }, [pathname]);
  return null;
}

// 404 — the radar found nothing. Clicking the dish pings it anyway — it still
// finds nothing here, which is the joke (and the product hook).
const NOPINGS = ['still nothing', 'nope — empty', 'just static', 'your footage isn’t here'];
function NotFound() {
  useEffect(() => { document.title = 'No signal — ClipRadar'; }, []);
  const [ping, setPing] = useState<{ x: number; y: number; n: number } | null>(null);
  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <SiteNav routed />
      <main style={{ flex: 1, display: 'grid', placeItems: 'center', position: 'relative', overflow: 'hidden', padding: '140px 24px 80px' }}>
        <div aria-hidden="true" style={{ position: 'absolute', inset: 0, background: 'var(--bg-grid)', maskImage: 'radial-gradient(ellipse at center, black, transparent 70%)', WebkitMaskImage: 'radial-gradient(ellipse at center, black, transparent 70%)', pointerEvents: 'none' }}></div>
        <div className="crw-page-in" style={{ position: 'relative', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
          <div
            aria-hidden="true"
            onClick={(e) => {
              // reduced motion: no ping (its animation is the whole payload; a
              // frozen ring would just stick to the dish)
              if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
              const r = e.currentTarget.getBoundingClientRect();
              setPing((p) => ({ x: e.clientX - r.left, y: e.clientY - r.top, n: (p?.n ?? 0) + 1 }));
            }}
            style={{ position: 'relative', width: 140, height: 140, marginBottom: 6, cursor: 'crosshair' }}
          >
            {[1, 0.7, 0.4].map((s, i) => (
              <span key={i} style={{ position: 'absolute', inset: `${(1 - s) * 50}%`, borderRadius: '50%', border: '1px solid rgba(74,227,255,0.18)' }}></span>
            ))}
            <span className="crw-sweep" style={{ position: 'absolute', inset: 0, opacity: 0.6 }}></span>
            {ping ? (
              <span key={ping.n} style={{ position: 'absolute', left: ping.x, top: ping.y, pointerEvents: 'none' }}>
                <i className="crw-oneping"></i>
                <i className="crw-pingdot"></i>
                <i className="crw-pinglabel">{NOPINGS[(ping.n - 1) % NOPINGS.length]}</i>
              </span>
            ) : null}
          </div>
          <span className="cr-label" style={{ color: 'var(--signal-400)' }}>404 · No signal</span>
          <h1 style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: 'clamp(30px, 4.6vw, 46px)', fontWeight: 700, letterSpacing: 'var(--tracking-display)' }}>
            Nothing on this frequency.
          </h1>
          <p style={{ margin: 0, maxWidth: 380, fontSize: 14.5, lineHeight: 1.65, color: 'var(--fg-2)' }}>
            The page you're scanning for doesn't exist — or it moved off-screen. The radar works much better on footage.
          </p>
          <Link to="/" style={{ marginTop: 8, fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--signal-400)', textDecoration: 'none' }}>
            ← Back to base
          </Link>
        </div>
      </main>
      <BigFooter />
    </div>
  );
}

// SEO landing pages (comparison + blog) — content-as-data in src/seo-pages.
function ComparePage() {
  const { slug } = useParams();
  const page = slug ? pageBySlug('compare', slug) : undefined;
  return page ? <SeoPageView page={page} /> : <NotFound />;
}

function BlogPost() {
  const { slug } = useParams();
  const page = slug ? pageBySlug('blog', slug) : undefined;
  return page ? <SeoPageView page={page} /> : <NotFound />;
}

// a quiet hello for anyone who opens the console — the one place on the site
// where plain text is the native material (night owls get an extra line)
const hour = new Date().getHours();
console.log(
  '%c◉ ClipRadar%c — you’re scanning the console.\n' +
    'Your footage deserves this kind of searchability — every frame, every word, every face, indexed locally.\n' +
    (hour >= 0 && hour < 5 ? 'Editing at this hour? The radar never sleeps either.\n' : '') +
    '→ https://getclipradar.com/trial  ·  → https://discord.gg/7aAtq7a2e9',
  'color:#6EC8FF;font-weight:700;font-family:ui-monospace,monospace;font-size:13px',
  'color:inherit;font-family:ui-monospace,monospace',
);

// ---- easter eggs: the "/" reflex and the Konami sonar burst ----
// Both are rare-tier discoveries: non-blocking (pointer-events: none), quiet,
// and skipped entirely under prefers-reduced-motion (the toast still shows —
// it's a static fade; only the pings are motion-payload).
const KONAMI = ['ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight', 'b', 'a'];
const BURST: { x: string; y: string; label: string }[] = [
  { x: '18%', y: '28%', label: 'contact' },
  { x: '72%', y: '22%', label: 'contact' },
  { x: '55%', y: '64%', label: 'your missing b-roll' },
  { x: '30%', y: '72%', label: 'not a UFO' },
  { x: '82%', y: '58%', label: 'just static' },
];

function Eggs() {
  const [toast, setToast] = useState<'in' | 'out' | null>(null);
  const [burst, setBurst] = useState(0); // >0 = burst id, re-fireable
  const konamiAt = useRef(0);

  useEffect(() => {
    const isTyping = (t: EventTarget | null) => {
      const el = t as HTMLElement | null;
      return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
    };
    const onKey = (e: KeyboardEvent) => {
      if (isTyping(e.target)) { konamiAt.current = 0; return; }
      // the "/" reflex — editors' search muscle memory, once per session
      // (sessionStorage can throw under strict privacy settings — egg skipped)
      try {
        if (e.key === '/' && !sessionStorage.getItem('crw-egg-slash')) {
          sessionStorage.setItem('crw-egg-slash', '1');
          setToast('in');
          window.setTimeout(() => setToast('out'), 3400);
          window.setTimeout(() => setToast(null), 3700);
        }
      } catch { /* no storage, no egg */ }
      // konami tracker
      const want = KONAMI[konamiAt.current];
      if (e.key === want || e.key.toLowerCase() === want) {
        konamiAt.current += 1;
        if (konamiAt.current === KONAMI.length) {
          konamiAt.current = 0;
          console.log('%c↑↑↓↓←→←→BA%c — the radar salutes you.', 'color:#6EC8FF;font-family:ui-monospace,monospace', 'color:inherit');
          if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            setBurst((b) => b + 1);
            window.setTimeout(() => setBurst(0), 2400);
          }
        }
      } else {
        konamiAt.current = e.key === KONAMI[0] ? 1 : 0;
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  return (
    <>
      {toast ? (
        <div aria-hidden="true" style={{
          position: 'fixed', left: '50%', bottom: 30, zIndex: 90, pointerEvents: 'none',
          transform: 'translateX(-50%)', display: 'flex', alignItems: 'center', gap: 9,
          padding: '10px 16px', borderRadius: 11,
          background: 'linear-gradient(180deg, rgba(16,16,20,0.97), rgba(9,9,12,0.97))',
          border: '1px solid rgba(255,255,255,0.10)',
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08), 0 20px 50px rgba(0,0,0,0.6)',
          fontFamily: 'var(--font-mono)', fontSize: 11.5, letterSpacing: '0.04em', color: 'var(--fg-2)',
          opacity: toast === 'in' ? 1 : 0, transition: 'opacity 260ms var(--ease-out)',
          animation: 'crw-pop 420ms var(--ease-out) backwards',
        }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', flex: 'none', background: 'var(--signal-400)', boxShadow: '0 0 8px var(--signal-glow)' }}></span>
          Nice reflex — <b style={{ color: 'var(--fg-1)', fontWeight: 600 }}>/</b> opens search in the app. This is just the website.
        </div>
      ) : null}
      {burst > 0 ? (
        <div key={burst} aria-hidden="true" style={{ position: 'fixed', inset: 0, zIndex: 85, pointerEvents: 'none' }}>
          {BURST.map((p, i) => (
            <span key={i} style={{ position: 'absolute', left: p.x, top: p.y, animationDelay: `${i * 140}ms` }}>
              <i className="crw-oneping" style={{ animationDelay: `${i * 140}ms` }}></i>
              <i className="crw-pingdot" style={{ animationDelay: `${i * 140}ms` }}></i>
              <i className="crw-pinglabel" style={{ animationDelay: `${i * 140}ms` }}>{p.label}</i>
            </span>
          ))}
        </div>
      ) : null}
    </>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <BrowserRouter>
      <ScrollManager />
      <TrackManager />
      <ConsentBanner />
      <Eggs />
      <Routes>
        <Route path="/" element={<App />} />
        <Route path="/docs" element={<DocsShell />}>
          {DOCS.flatMap((c) => c.pages).map((p) =>
            p.slug === ''
              ? <Route key="index" index element={<DocPageView page={p} />} />
              : <Route key={p.slug} path={p.slug} element={<DocPageView page={p} />} />,
          )}
        </Route>
        <Route path="/download" element={<DownloadPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/legal/eula" element={<EulaPage />} />
        <Route path="/legal/privacy" element={<PrivacyPage />} />
        <Route path="/legal/refund" element={<RefundPage />} />
        <Route path="/joined" element={<JoinedPage />} />
        <Route path="/thanks" element={<ThanksPage />} />
        <Route path="/account" element={<AccountPage />} />
        <Route path="/trial" element={<TrialPage />} />
        <Route path="/affiliates" element={<AffiliatesPage />} />
        <Route path="/notme" element={<NotMePage />} />
        <Route path="/variants" element={<VariantsPage />} />
        {import.meta.env.DEV ? <Route path="/scene-lab" element={<SceneLabPage />} /> : null}
        <Route path="/compare" element={<CompareIndex />} />
        <Route path="/compare/:slug" element={<ComparePage />} />
        <Route path="/blog" element={<BlogIndex />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  </StrictMode>,
);
